import { Combobox, Label, makeStyles, Option, OptionGroup, Tooltip, useId } from '@fluentui/react-components';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnterAmountsDialogComboboxData } from '../../hooks/useEnterAmountsDialogComboboxData';
import { useEnterAmountsDialogStyles } from '../../hooks/useEnterAmountsDialogStyles';
import { useHiddenComboboxCheckIconStyles } from '../../hooks/useHiddenComboboxCheckIconStyles';
import { IEntity } from '../../model';
import {
    EnterAmountsDialogFiltersActionType,
    IEnterAmountsDialogComboboxPropsBase,
    IEnterAmountsDialogFilterEntityChangeAction,
} from '../../reducers/enterAmountsDialogFiltersReducer';
import { useVirtualizedCombobox, VirtualizedOptionList } from '../common/VirtualizedOptionList';

export const findEntityDtoByCode = (entities: IEntity[] | null | undefined, entityCode: string | undefined): IEntity | undefined => {
    return entities?.find((entity: IEntity) => entity.code === entityCode);
};

export const findEntitiesByDisplayValue = (displayValue: string, entities: IEntity[]): IEntity[] => {
    return entities.filter((entity: IEntity) => `${entity.code} - ${entity.name}`.toLowerCase().includes(displayValue.toLowerCase()));
};

const useEntityComboboxStyles = makeStyles({
    optionGroupFlexWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        overflow: 'hidden',
    },
    entityCodeTooltipWrapper: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: '0 0 80px',
    },
    entityNameTooltipWrapper: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: '3 0 0',
    },
});

const OptionGroupLabel: FC = () => {
    const { t } = useTranslation();
    const { optionGroupFlexWrapper, entityCodeTooltipWrapper, entityNameTooltipWrapper } = useEntityComboboxStyles();
    return (
        <div className={optionGroupFlexWrapper}>
            <div className={entityCodeTooltipWrapper}>{t('entityCode')}</div>
            <div className={entityNameTooltipWrapper}>{t('entityName')}</div>
        </div>
    );
};

interface IEntityComboboxOptionProps {
    entity: IEntity;
    optionToSelectedOption: (entity: IEntity) => string;
    optionToInputValue: (entity: IEntity) => string;
}

export interface IEnterAmountsDialogEntityComboboxProps
    extends IEnterAmountsDialogComboboxPropsBase<IEntity, IEnterAmountsDialogFilterEntityChangeAction> {}

const EntityComboboxOption: FC<IEntityComboboxOptionProps> = (props) => {
    const { hidden: hiddenCheckBoxCssClass } = useHiddenComboboxCheckIconStyles();
    const { optionGroupFlexWrapper, entityCodeTooltipWrapper, entityNameTooltipWrapper } = useEntityComboboxStyles();
    const { entity, optionToSelectedOption, optionToInputValue } = props;
    return (
        <Option
            className={hiddenCheckBoxCssClass}
            key={optionToSelectedOption(entity)}
            text={optionToInputValue(entity)}
            value={optionToSelectedOption(entity)}
            aria-label={optionToInputValue(entity)}
        >
            <div className={optionGroupFlexWrapper}>
                {/* positioning={{ pinned: true }} is used to prevent the tooltip from "floating" when using virtualization */}
                <Tooltip
                    content={entity!.code!}
                    relationship='inaccessible'
                    appearance='inverted'
                    withArrow={true}
                    positioning={{ pinned: true }}
                >
                    <div className={entityCodeTooltipWrapper}>{entity.code}</div>
                </Tooltip>
                <Tooltip
                    content={entity!.name!}
                    relationship='inaccessible'
                    appearance='inverted'
                    withArrow={true}
                    positioning={{ pinned: true }}
                >
                    <div className={entityNameTooltipWrapper}>{entity.name}</div>
                </Tooltip>
            </div>
        </Option>
    );
};

export const EntityCombobox: FC<IEnterAmountsDialogEntityComboboxProps> = (props: IEnterAmountsDialogEntityComboboxProps) => {
    const { t } = useTranslation();
    const optionToInputValue: (option: IEntity) => string = (option: IEntity) => `${option.code} - ${option.name}`;
    const optionToSelectedOption: (option: IEntity) => string = (option: IEntity) => option.code!;
    const findOptionsByDisplayValue: (displayValue: string, options: IEntity[]) => IEntity[] = (displayValue: string, options: IEntity[]) =>
        findEntitiesByDisplayValue(displayValue, options);
    const findOptionByKey: (key: string | undefined, options: IEntity[]) => IEntity | undefined = (
        key: string | undefined,
        options: IEntity[]
    ) => findEntityDtoByCode(options, key);
    const entityLabelId: string = useId('entity');
    const { comboboxStyles } = useEnterAmountsDialogStyles();
    const optionRenderer: (entity: IEntity) => JSX.Element = useCallback(
        (entity: IEntity) => (
            <EntityComboboxOption
                key={optionToSelectedOption(entity)}
                entity={entity}
                optionToSelectedOption={optionToSelectedOption}
                optionToInputValue={optionToInputValue}
            />
        ),
        [optionToSelectedOption, optionToInputValue]
    );
    const {
        inputValue,
        selectedOptions,
        matchingOptionsData,
        searchText,
        onOptionSelect: handleDataOptionSelect,
        onBlur,
        onChange,
        disabled,
        onKeyDown,
        comboboxRef,
    } = useEnterAmountsDialogComboboxData<IEntity, IEnterAmountsDialogFilterEntityChangeAction, IEnterAmountsDialogEntityComboboxProps>(
        props,
        {
            optionToInputValue,
            optionToSelectedOption,
            findOptionsByDisplayValue: findOptionsByDisplayValue,
            findOptionByKey,
        },
        EnterAmountsDialogFiltersActionType.EntityChanged
    );

    const virtualizerConfig = useVirtualizedCombobox({
        selectedOptions,
        matchingOptionsData,
        optionToSelectedOption,
        onOptionSelect: handleDataOptionSelect,
    });
    const {
        scrollRef,
        listboxClassName,
        onOptionSelect: handleVirtualizedOptionSelect,
        onOpenChange: handleVirtualizedOpenChange,
    } = virtualizerConfig;

    const optionGroupLabel: JSX.Element = useMemo(() => <OptionGroupLabel />, []);
    return (
        <div>
            <Label id={entityLabelId} weight='semibold'>
                {t('entity')}
            </Label>
            <Combobox
                className={comboboxStyles}
                aria-labelledby={entityLabelId}
                selectedOptions={selectedOptions}
                value={inputValue}
                onOptionSelect={handleVirtualizedOptionSelect}
                onOpenChange={handleVirtualizedOpenChange}
                disabled={disabled}
                freeform
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                ref={comboboxRef}
                clearable={props.clearable}
                listbox={{
                    ref: scrollRef,
                    className: listboxClassName,
                }}
            >
                {searchText ? (
                    <Option aria-disabled='true' key='freeform' text={searchText} disabled={true}>
                        {t('noOptionsFound', { searchText: searchText })}
                    </Option>
                ) : (
                    <OptionGroup key='optionGroup' label={optionGroupLabel}>
                        <VirtualizedOptionList
                            items={matchingOptionsData}
                            renderOption={optionRenderer}
                            virtualizerConfig={virtualizerConfig}
                        />
                    </OptionGroup>
                )}
            </Combobox>
        </div>
    );
};
